import * as React from "react";
import { cn } from "../../lib/utils";

const SCROLL_THRESHOLD = 50;

export interface ConversationScrollButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  visible?: boolean;
  onClick?: () => void;
}

const ConversationScrollButton = React.forwardRef<
  HTMLButtonElement,
  ConversationScrollButtonProps
>(({ className, visible = false, onClick, ...props }, ref) => {
  if (!visible) return null;

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={cn(
        "absolute bottom-4 right-4 z-10 flex h-9 w-9 items-center justify-center rounded-full",
        "bg-primary text-primary-foreground shadow-lg border border-border/50",
        "hover:bg-primary/90 transition-all duration-150",
        "animate-in fade-in-0 zoom-in-95",
        className
      )}
      aria-label="滚动到底部"
      {...props}
    >
      <svg
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 14l-7 7m0 0l-7-7m7 7V3"
        />
      </svg>
    </button>
  );
});
ConversationScrollButton.displayName = "ConversationScrollButton";

export interface ConversationProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
}

const Conversation = React.forwardRef<HTMLDivElement, ConversationProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col h-full overflow-hidden bg-background relative",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
Conversation.displayName = "Conversation";

const ConversationHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center justify-between border-b border-border/50 px-4 py-3",
      className
    )}
    {...props}
  >
    {children}
  </div>
));
ConversationHeader.displayName = "ConversationHeader";

export interface ConversationMessagesProps
  extends React.HTMLAttributes<HTMLDivElement> {
  autoScroll?: boolean;
  onStickyChange?: (sticky: boolean) => void;
}

const ConversationMessages = React.forwardRef<
  HTMLDivElement,
  ConversationMessagesProps
>(({ className, children, autoScroll = true, onStickyChange, ...props }, ref) => {
  const internalRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = (ref as React.RefObject<HTMLDivElement>) || internalRef;
  const isStickyRef = React.useRef(true);
  const stickyLockRef = React.useRef(false);

  const checkSticky = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const atBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;

    if (atBottom !== isStickyRef.current) {
      isStickyRef.current = atBottom;
      onStickyChange?.(atBottom);
    }
  }, [scrollRef, onStickyChange]);

  const scrollToBottom = React.useCallback((smooth = true) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: smooth ? "smooth" : "instant",
    });
  }, [scrollRef]);

  React.useEffect(() => {
    if (!autoScroll) return;
    const el = scrollRef.current;
    if (!el) return;

    const observer = new MutationObserver(() => {
      if (isStickyRef.current && !stickyLockRef.current) {
        scrollToBottom();
      }
    });

    observer.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, [autoScroll, scrollRef, scrollToBottom]);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      checkSticky();
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [scrollRef, checkSticky]);

  return (
    <div
      ref={scrollRef}
      className={cn(
        "flex-1 overflow-y-auto overflow-x-hidden scroll-smooth",
        className
      )}
      {...props}
    >
      <div className="divide-y divide-border/30 relative">{children}</div>
    </div>
  );
});
ConversationMessages.displayName = "ConversationMessages";

const ConversationInput = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "border-t border-border/50 bg-muted/30 px-4 py-3",
      className
    )}
    {...props}
  >
    {children}
  </div>
));
ConversationInput.displayName = "ConversationInput";

const ConversationFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "border-t border-border/50 px-4 py-2 text-xs text-muted-foreground",
      className
    )}
    {...props}
  >
    {children}
  </div>
));
ConversationFooter.displayName = "ConversationFooter";

export {
  Conversation,
  ConversationHeader,
  ConversationMessages,
  ConversationInput,
  ConversationFooter,
  ConversationScrollButton,
};
